import { CUSTOMER_SOURCE_CONFIG } from 'core/constants/checkout.constants';
import { EDistributorSource } from 'core/model/enums/checkout.enum';
import { ECustomerSource } from 'core/model/enums/customer.enum';
import { ICheckoutDetail, ICheckoutDetailGroup, TDistributorSource } from 'core/model/interfaces/checkout.interface';
import { ICartProduct } from 'core/model/interfaces/product.interface';
import { getPresentationTextForDetail } from 'shared/utils/products.utils';
import { ICurrencyDTO } from '../catalogs/catalogs.dto';
import { IDeletedProductDTO } from '../shopping-cart/shopping-cart.dto';
import { ICheckoutCartDetailDTO, ICheckoutDetailGroupDTO, IDeliveryGroupingRuleItemDTO } from './checkout.dto';

const createInitialCheckoutDetailGroup = (distributor: TDistributorSource): ICheckoutDetailGroup => ({
  amounts: {
    base: 0,
    final: 0,
    igv: 0,
    perception: 0,
    price: 0,
    discount: 0,
  },
  groupingRule: {
    dates: [],
    products: [],
    distributor,
    paymentMethods: [],
  },
});

export const mapToCheckoutDetail = (data: ICheckoutCartDetailDTO, customerSource: ECustomerSource): ICheckoutDetail => {
  const currency: ICurrencyDTO = {
    code: data.currency.code,
    symbol: data.currency.symbol,
  };

  const cartDetails = {
    alicorpCart: createInitialCheckoutDetailGroup('alicorpCart'),
    alliedCart: createInitialCheckoutDetailGroup('alliedCart'),
    generalCart: createInitialCheckoutDetailGroup('generalCart'),
  };

  const config = CUSTOMER_SOURCE_CONFIG[customerSource];

  config?.forEach(({ source, cart, distributor, sourceId }) => {
    if (data[source]) {
      cartDetails[cart] = mapSource(data[source], distributor, currency, sourceId);
    }
  });

  return {
    ...cartDetails,
    amounts: data.totals,
    deletedProducts: mapDeletedProducts(data.deletedItems),
    isPurchaseBlocked: data.isPurchaseBlocked,
  };
};

const mapDeletedProducts = (deletedProductsByStock?: Array<IDeletedProductDTO>) =>
  deletedProductsByStock?.map(dp => ({
    displayName: dp.product.name,
    sku: dp.id,
    image: dp.product.imageUrl,
    presentation: dp.product.presentation.pluralName,
  })) ?? [];

const mapSource = (
  data: ICheckoutDetailGroupDTO,
  distributor: EDistributorSource,
  currency: ICurrencyDTO,
  sourceId: ECustomerSource,
): ICheckoutDetailGroup => ({
  amounts: data.cart.totals,
  groupingRule: {
    dates: data.deliveryDates,
    distributor,
    products: data.cart.items.map(item => mapGroupingRuleProduct(item, currency)),
    paymentMethods: data.paymentMethods.map(pm => ({
      id: pm.id,
      type: pm.name,
      code: pm.type,
      description: pm.name,
      creditLine: pm.creditLine
        ? {
            ...pm.creditLine,
            canPurchase: true,
            sourceId,
          }
        : undefined,
    })),
  },
});

const mapGroupingRuleProduct = (item: IDeliveryGroupingRuleItemDTO, currency: ICurrencyDTO): ICartProduct => {
  const promos = item.promotions
    ? item.promotions.map(promo => ({
        id: `${promo.id}`,
        name: promo.description,
        value: promo.discount || undefined,
      }))
    : [];

  const { presentation: unitMeasure } = item.product;

  return {
    amounts: {
      base: item.totals.base,
      final: item.totals.final,
      igv: item.totals.igv,
      perception: item.totals.perception,
      price: item.totals.price,
      discount: item.totals.discount,
    },
    brand: item.product.brand.name,
    categoryId: Number(item.product.category.id),
    categoryName: item.product.category.name,
    distributorName: item.product.distributor.name,
    currency: {
      id: currency.code,
      symbol: currency.symbol,
    },
    promos,
    eanCode: item.product.ean,
    image: item.product.imageUrl,
    name: item.product.name,
    providerDisplayName: item.product.provider.name,
    quantity: item.requestedQuantity,
    sku: item.product.id,
    sourceId: 1,
    stock: item.product.presentation.stock,
    subcategoryId: Number(item.product.family.id),
    subcategoryName: item.product.family.name,
    reducedQuantity: item.reducedQuantity || 0,
    taxRate: 0,
    unitMeasure: {
      default: unitMeasure.isDefault,
      name: unitMeasure.name,
      pluralName: unitMeasure.pluralName,
      code: unitMeasure.id,
      price: item.product.price.amount,
      originalPrice: item.product.price.original,
      presentation: getPresentationTextForDetail(unitMeasure),
      promoIndicators: {
        hasDiscount: unitMeasure.promotions.hasDiscount,
      },
    },
    metadata: {
      index: item.metadata?.index ?? 1,
      itemListName: item.metadata?.itemListName,
      origin: item.metadata?.origin,
    },
  };
};
